import React, { useEffect, useMemo, useState } from "react";
import * as Scrivito from "scrivito";
import cx from "classnames";
import { getHomepage } from "utils";
import { Select } from "components/Select/Select";
import s from "components/ProductConfigurator/ProductConfigurator.module.scss";
import { useProductOutput } from "stores/product";
import { Open3dViewButton } from "./Open3dViewButton";
import { ImageCarousel } from "./ImageCarousal";
import { ResultsProductImage } from "./ResultsProductImage";

export const ProductImagesPreview = Scrivito.connect(
  ({ disable3dButton = false, carousel = false }) => {
    const productOutput = useProductOutput();
    const [selectedImageIndex, setSelectedImageIndex] = useState(0);

    const isProductAccessory =
      !!productOutput?.product?.accessoryInfo?.accessoryId;

    const images = useMemo(
      () => productOutput?.productImageList?.productImages ?? [],
      [productOutput],
    );

    const defaultImage = useScrivitoDefaultImage();

    const handleSelectImage = (
      option: { value: string; label: string } | null,
    ) => {
      if (!option) {
        return;
      }
      const index = images.findIndex((image) => image?.pimId === option.value);
      if (index !== -1) {
        setSelectedImageIndex(index);
      }
    };

    useEffect(() => {
      setSelectedImageIndex(0);
    }, [images]);

    const options = useMemo(
      () =>
        images &&
        images.map((image) => ({
          label: image?.text || "",
          value: image?.pimId ?? "",
        })),
      [images],
    );

    const selectedOption = options.find(
      (_option, index) => index === selectedImageIndex,
    );

    if (!images) {
      return <></>;
    }

    const selectedImage = images[selectedImageIndex];

    return (
      <div
        className={cx(
          "m-0 h-100 w-100 d-flex flex-column justify-content-center",
          s.cardContainer,
          {
            "align-items-center": !carousel,
            "align-items-start": carousel,
          },
        )}
      >
        <div className={cx("pb-2 d-flex gap-2 align-items-start w-100")}>
          <div className="w-100">
            <Select
              options={options}
              onChange={handleSelectImage}
              value={selectedOption}
              name="imageSelect"
              testId="image-select"
              className="font-md"
              menuPlacement="auto"
              menuPortalTarget={document.body}
              menuPosition="fixed"
              isDisabled={isProductAccessory}
            />
          </div>
          {!disable3dButton && (
            <div className={cx("flex-shrink-0", s.topButton)}>
              <Open3dViewButton />
            </div>
          )}
        </div>
        {carousel ? (
          <ImageCarousel
            selectedImageIndex={selectedImageIndex}
            images={images}
            setSelectedImageIndex={setSelectedImageIndex}
            defaultImage={defaultImage ?? undefined}
            disabled={isProductAccessory}
            productName={productOutput?.product?.orderCode?.key ?? ""}
          />
        ) : (
          selectedImage && (
            <ResultsProductImage
              defaultImage={defaultImage ?? undefined}
              image={selectedImage}
              width={400}
              height={320}
              productName={productOutput?.product?.orderCode?.key ?? ""}
              disabled={isProductAccessory}
            />
          )
        )}
      </div>
    );
  },
);

const useScrivitoDefaultImage = () => {
  const defaultImage = useMemo(() => {
    const productListPage = getHomepage()?.get("productListPage");
    if (!productListPage || typeof productListPage !== "object") {
      return null;
    }
    if (!("get" in productListPage)) {
      return null;
    }
    return productListPage.get("defaultProductImage") as Scrivito.Obj;
  }, []);

  return defaultImage;
};
